import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Loader2, CheckCircle2, Clock } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { SectionHeading } from "./SectionHeading";
import { SERVICE_OPTIONS, CONDITION_OPTIONS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  phone: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  service: "",
  condition: "",
  notes: "",
};

export const Booking = () => {
  const [form, setForm] = useState(initialState);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const loadSlots = useCallback(async (selectedDate, service, showLoader = true) => {
    if (!selectedDate) return;
    if (showLoader) setSlotsLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await axios.get(`${API}/availability`, { params: { date: dateStr, service: service || undefined } });
      setSlots(res.data.slots);
      setSelectedTime((current) => current && !res.data.slots.some((slot) => slot.time === current && slot.available) ? "" : current);
    } catch (e) {
      toast.error("Could not load available times. Please try again.");
    } finally {
      if (showLoader) setSlotsLoading(false);
    }
  }, []);

  const onPickDate = async (d) => {
    setDate(d);
    setSelectedTime("");
    setSlots([]);
    await loadSlots(d, form.service);
  };

  useEffect(() => {
    if (!date) return undefined;
    const refresh = () => loadSlots(date, form.service, false);
    const interval = window.setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [date, form.service, loadSlots]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !date || !selectedTime) {
      toast.error("Please fill in name, phone, service, date and a time slot.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: selectedTime,
      });
      setConfirmation(res.data);
      toast.success("Appointment booked! Check your confirmation below.");
      setForm(initialState);
      setDate(null);
      setSlots([]);
      setSelectedTime("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      if (err.response?.status === 409 && date) {
        onPickDate(date); // refresh slots
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0a0a0a] border-white/10 text-white placeholder:text-zinc-600 focus:border-white focus-visible:ring-1 focus-visible:ring-white h-12";

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative z-10 py-24 md:py-32 bg-[#0a0a0a] border-y border-white/5 scroll-mt-20"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <SectionHeading
          overline="Book Your Detail"
          title="Schedule your appointment"
          subtitle="Pick a service, choose a date and an open time slot. We'll confirm your appointment and provide a personalized quote. Hours: Mon–Fri 10am–5pm · Sat–Sun 8am–8pm."
          align="center"
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-white/10 bg-[#111111] p-7 md:p-10"
        >
          {confirmation ? (
            <div data-testid="booking-success" className="py-10 text-center">
              <CheckCircle2 size={54} className="mx-auto text-white mb-6" />
              <h3 className="font-heading text-2xl font-semibold text-white">
                Appointment Requested
              </h3>
              <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                Thanks for choosing Marez Detailing. We'll call you shortly to confirm.
              </p>
              <div className="mt-6 inline-flex flex-col items-center rounded-2xl border border-white/15 bg-white/5 px-8 py-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Confirmation #
                </span>
                <span data-testid="confirmation-number" className="font-heading text-3xl font-bold text-white mt-1">
                  {confirmation.confirmation_number}
                </span>
                <span className="mt-3 text-sm text-zinc-400">
                  {confirmation.service} · {confirmation.preferred_date} at {confirmation.preferred_time}
                </span>
              </div>
              <div>
                <button
                  data-testid="booking-another-btn"
                  onClick={() => setConfirmation(null)}
                  className="mt-8 rounded-full border border-white/20 text-white px-6 py-3 hover:bg-white/5 transition-colors"
                >
                  Book Another Detail
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-zinc-300">Name *</Label>
                <Input
                  data-testid="booking-name"
                  className={inputCls}
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Phone Number *</Label>
                <Input
                  data-testid="booking-phone"
                  className={inputCls}
                  placeholder="(510) 555-0000"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Vehicle Year</Label>
                <Input
                  data-testid="booking-year"
                  className={inputCls}
                  placeholder="2022"
                  value={form.vehicle_year}
                  onChange={(e) => set("vehicle_year", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Make</Label>
                  <Input
                    data-testid="booking-make"
                    className={inputCls}
                    placeholder="Porsche"
                    value={form.vehicle_make}
                    onChange={(e) => set("vehicle_make", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Model</Label>
                  <Input
                    data-testid="booking-model"
                    className={inputCls}
                    placeholder="911"
                    value={form.vehicle_model}
                    onChange={(e) => set("vehicle_model", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Service Wanted *</Label>
                <Select value={form.service} onValueChange={(v) => {
                  set("service", v);
                  if (date) loadSlots(date, v);
                }}>
                  <SelectTrigger data-testid="booking-service" className={inputCls}>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-white/10 text-white">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem data-testid={`booking-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={s} value={s} className="focus:bg-white/10 focus:text-white">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Vehicle Condition</Label>
                <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                  <SelectTrigger data-testid="booking-condition" className={inputCls}>
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-white/10 text-white">
                    {CONDITION_OPTIONS.map((c) => (
                      <SelectItem data-testid={`booking-condition-option-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={c} value={c} className="focus:bg-white/10 focus:text-white">
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="text-zinc-300">Preferred Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date"
                      className={`${inputCls} w-full flex items-center justify-between rounded-md px-3 text-left ${
                        date ? "text-white" : "text-zinc-600"
                      }`}
                    >
                      {date ? format(date, "PPP") : "Pick a date"}
                      <CalendarIcon size={17} className="text-zinc-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#111111] border-white/10" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={onPickDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time slots */}
              {date && (
                <div className="md:col-span-2 space-y-3">
                  <Label className="text-zinc-300 flex items-center gap-2">
                    <Clock size={15} /> Available Times *
                  </Label>
                  {slotsLoading ? (
                    <div className="flex items-center gap-2 text-zinc-500 text-sm py-3">
                      <Loader2 size={16} className="animate-spin" /> Loading times...
                    </div>
                  ) : (
                    <div data-testid="time-slots" className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          type="button"
                          data-testid={`slot-${s.time.replace(/[:\s]/g, "")}`}
                          disabled={!s.available}
                          onClick={() => setSelectedTime(s.time)}
                          className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-colors border ${
                            selectedTime === s.time
                              ? "bg-[#fafafa] text-[#050505] border-white"
                              : s.available
                              ? "bg-[#0a0a0a] text-zinc-200 border-white/10 hover:border-white/40"
                              : "bg-[#0a0a0a] text-zinc-700 border-white/5 line-through cursor-not-allowed"
                          }`}
                        >
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="md:col-span-2 space-y-2">
                <Label className="text-zinc-300">Additional Notes</Label>
                <Textarea
                  data-testid="booking-notes"
                  className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-zinc-600 focus:border-white focus-visible:ring-1 focus-visible:ring-white min-h-[90px]"
                  placeholder="Anything else we should know?"
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                />
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={loading}
                  className="btn-3d gloss-sweep w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-8 py-4 hover:bg-white disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Booking...
                    </>
                  ) : (
                    "Schedule Your Detail"
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
